import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAdminApi } from "./useAdminApi";
import { ImageField } from "./ImageField";
import { assetUrl, errMsg, fmtDate, money } from "@/lib/api";

const PUB = ["published", "Visible on site", "switch"];
const ORDER = ["order", "Sort order", "number"];
export const COLLECTION_CONFIG = {
  events: { title: "title", sub: (x) => `${fmtDate(x.date)} · ${x.category}`, img: "image", fields: [["title", "Title"], ["category", "Category", "select", ["Poetry Club", "Workshop", "Open Mic", "Literacy", "Def Street", "Other"]], ["date", "Date", "date"], ["time", "Time"], ["location", "Location"], ["presenter", "Presenter / host (shown on schedule tile)"], ["color", "Schedule tile color", "select", ["auto", "aqua", "passion", "lav", "tang", "tea", "mustard", "quartz", "char"]], ["icon", "Schedule tile icon", "select", ["auto", "mic", "pen", "book", "megaphone", "feather", "heart", "star", "music"]], ["description", "Description", "textarea"], ["image", "Image", "image"], PUB, ORDER] },
  ticket_tiers: { title: "name", sub: (x) => `${money(x.price, true)} · capacity ${x.capacity || "unlimited"}`, fields: [["name", "Name"], ["price", "Price ($)", "number"], ["capacity", "Capacity (0 = unlimited)", "number"], ["description", "Description", "textarea"], PUB, ORDER] },
  sponsorships: { title: "name", sub: (x) => `${x.category} · ${money(x.price)}`, fields: [["name", "Name"], ["category", "Category", "select", ["Workshop", "Program", "Def Street"]], ["price", "Price ($)", "number"], ["description", "Description", "textarea"], ["perks", "Perks (one per line)", "lines"], PUB, ORDER] },
  testimonials: { title: "author", sub: (x) => x.quote, fields: [["quote", "Quote", "textarea"], ["author", "Author"], ["role", "Role"], PUB, ORDER] },
  poets: { title: "name", sub: (x) => x.bio, img: "image", fields: [["name", "Name"], ["bio", "Bio", "textarea"], ["image", "Photo", "image"], ["link", "Link"], PUB, ORDER] },
  gallery: { title: "caption", sub: () => "", img: "url", fields: [["url", "Photo", "image"], ["caption", "Caption"], PUB, ORDER] },
  team: { title: "name", sub: (x) => x.title, img: "image", fields: [["name", "Name"], ["title", "Title"], ["description", "Description", "textarea"], ["image", "Photo", "image"], ["email", "Contact email", "email"], PUB, ORDER] },
};

const blankFor = (fields) => Object.fromEntries(fields.map(([k, , t, opts]) => [k, t === "switch" ? true : t === "number" ? 0 : t === "lines" ? [] : t === "select" ? opts[0] : ""]));

const Field = ({ f, v, set }) => {
  const [k, label, type, opts] = f;
  const tid = `admin-form-${k}`;
  let input;
  if (type === "textarea") input = <textarea rows={3} className="field" value={v[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={tid} />;
  else if (type === "select") input = <select className="field" value={v[k]} onChange={(e) => set(k, e.target.value)} data-testid={tid}>{opts.map((o) => <option key={o}>{o}</option>)}</select>;
  else if (type === "switch") input = <input type="checkbox" checked={!!v[k]} onChange={(e) => set(k, e.target.checked)} className="w-5 h-5 accent-[#C85A32]" data-testid={tid} />;
  else if (type === "image") input = <ImageField value={v[k]} onChange={(x) => set(k, x)} testid={tid} />;
  else if (type === "lines") input = <textarea rows={3} className="field" value={(v[k] || []).join("\n")} onChange={(e) => set(k, e.target.value.split("\n"))} data-testid={tid} />;
  else input = <input type={type || "text"} className="field" value={v[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} data-testid={tid} />;
  return <label className={`block ${type === "switch" ? "flex items-center gap-3" : ""}`}><span className="text-xs font-semibold text-mas-mute block mb-1">{label}</span>{input}</label>;
};

export default function AdminCollection({ coll }) {
  const cfg = COLLECTION_CONFIG[coll];
  const api = useAdminApi();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api("get", `/admin/c/${coll}`).then(setItems), [api, coll]);
  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const { id, created_at, remaining, ...body } = edit;
    if (body.perks) body.perks = body.perks.filter((p) => p.trim());
    try {
      await api(id ? "put" : "post", id ? `/admin/c/${coll}/${id}` : `/admin/c/${coll}`, body);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async (it) => {
    if (!window.confirm("Delete this item?")) return;
    await api("delete", `/admin/c/${coll}/${it.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid={`admin-collection-${coll}`}>
      <button onClick={() => setEdit(blankFor(cfg.fields))} className="btn-terra mb-6" data-testid="admin-add-item-button"><Plus className="w-4 h-4" />Add new</button>
      {!items ? <div className="h-32 animate-pulse paper-card" /> : (
        <div className="bg-white border-2 border-mas-ink divide-y divide-mas-sand">
          {items.length === 0 && <p className="p-6 text-mas-mute text-sm">Nothing here yet.</p>}
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-4 p-4" data-testid={`admin-item-${it.id}`}>
              {cfg.img && it[cfg.img] && <img src={assetUrl(it[cfg.img])} alt="" className="w-14 h-14 object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{it[cfg.title] || "(untitled)"} {!it.published && <span className="ml-2 font-mono text-[10px] uppercase bg-mas-paper2 px-1.5 py-0.5">hidden</span>}</p>
                <p className="text-xs text-mas-mute truncate">{cfg.sub(it)}</p>
              </div>
              <button onClick={() => setEdit({ ...it })} className="p-2 hover:text-mas-terra" data-testid={`admin-edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(it)} className="p-2 text-mas-terra" data-testid={`admin-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="admin-scope bg-mas-cream text-ink max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-item-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{edit?.id ? "Edit" : "Add"}</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={save} className="space-y-4">
              {cfg.fields.map((f) => <Field key={f[0]} f={f} v={edit} set={(k, x) => setEdit({ ...edit, [k]: x })} />)}
              <button className="btn-terra w-full" data-testid="admin-item-save-button">Save</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
